import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

/**
 * API yanıtları `Cache-Control: no-store` (spec §4.9); tazelik SSE ile gelir (spec §4.6),
 * bu yüzden aralıklı yeniden çekme yoktur. 30 sn `staleTime` sekme dönüşlerinde
 * gereksiz isteği keser ama canlı olayı geciktirmez — olay geldiğinde sorgu geçersizlenir.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) =>
        // 4xx yeniden denenmez: yetki ve doğrulama hataları tekrarla düzelmez.
        failureCount < 2 &&
        !(
          typeof error === 'object' &&
          error !== null &&
          'status' in error &&
          Number((error as { status: number }).status) < 500
        ),
    },
  },
});

export const queryKeys = {
  me: ['me'] as const,
  patients: (q: string, filter: string) => ['patients', q, filter] as const,
  /**
   * Hasta listesi ekranının numaralı sayfası; `['patients']` önekiyle birlikte geçersiz kılınır.
   * Sayfa numarası anahtarın SONUNA `usePagedQuery` tarafından eklenir (data/paged-query.ts).
   */
  patientPage: (q: string, filter: string, sort: string, dir: string) =>
    ['patients', 'page', q, filter, sort, dir] as const,
  /**
   * Çip sayaçları TOPLAMDIR: arama ve süzgeç değişince değişmezler, bu yüzden listeden ayrı bir
   * anahtarları vardır. Anahtarın ikinci parçası bir NESNEDİR; dize olsaydı arama kutusuna aynı
   * sözcüğü yazan kullanıcı liste anahtarıyla çakışırdı (liste sonsuz sorgu, sayaç değil).
   */
  patientCount: (filter: string) => ['patients', { count: filter }] as const,
  /** B1 köprüsü: demo sayfalarının sayısal kimliği hasta koduyla aranır (app/legacy-patient-id.ts). */
  patientByCode: (code: string) => ['patients', { code }] as const,
  /**
   * Seçici ve komut paleti araması (data/patient-search.ts). Nesne parçası liste anahtarıyla
   * (`['patients', q, filter]`, sonsuz sorgu) çakışmasın diye vardır.
   */
  patientSearch: (q: string, limit: number) => ['patients', { search: q, limit }] as const,
  patient: (id: string) => ['patient', id] as const,
  documents: (id: string) => ['patient', id, 'documents'] as const,
  consents: (id: string) => ['patient', id, 'consents'] as const,
  /**
   * Sürüm 1 — hastanın anamnezi (`GET /patients/:id/health`: geçerli sürüm, geçmiş, şerit).
   * `patient` önekinin ALTINDADIR: hasta olayı ve anamnez olayı (`['patient', id]`) onu da tazeler.
   */
  patientHealth: (id: string) => ['patient', id, 'health'] as const,
  /**
   * Sürüm 1 — süreç şeridi ve Bekleyenler (`GET /patients/:id/flow`, C34). Plan, onam ve randevu
   * olayları hastayı söylemez; açık şeritleri yüklemle tazeler (data/events.ts).
   */
  patientFlow: (id: string) => ['patient', id, 'flow'] as const,
  /**
   * Sürüm 2 — hastanın ziyaretleri ("Muayeneler n", `GET /patients/:id/visits`). `patient` önekinin
   * ALTINDADIR: hasta olayı onu da tazeler; ziyaret ve diş kaydı olayları hastayı söyler.
   */
  patientVisits: (id: string) => ['patient', id, 'visits'] as const,
  /**
   * Sürüm 2 — bir ziyaretin ayrıntısı (`GET /visits/:id`: kayıtlar, notlar, röntgenler). Hasta
   * önekinin altında DEĞİLDİR (kimlik ziyaretindir); ziyaret, not ve diş kaydı olayları `['visit']`
   * önekiyle toptan tazeler (aynı anda açık muayene sayısı birkaç tanedir).
   */
  visit: (id: string) => ['visit', id] as const,
  /**
   * Sürüm 2 — hastanın klinik notları (`GET /patients/:id/notes`). Süzgeç (`NoteListQuery`)
   * anahtarın SONUNA eklenir (`data/clinical-notes.ts`); bu önek bütün süzgeçleri kapsar.
   */
  patientNotes: (id: string) => ['patient', id, 'notes'] as const,
  dataRequests: (patientId: string | null) => ['data-requests', patientId ?? 'all'] as const,
  /** Kullanıcı listesi tek parçadır: rol/aktiflik süzgeçleri istemcide uygulanır. */
  users: ['users'] as const,
  /** Sürüm 1 — Ayarlar › Roller ve yetkiler (`GET /roles`): roller, kişi sayıları, son değişiklikler. */
  roles: ['roles'] as const,
  units: ['units'] as const,
  /** Klinik ayarları (çalışma saatleri vb.): tek kayıt. */
  clinic: ['clinic'] as const,
  /** B2 — ağız haritası: hasta başına tek çizelge sorgusu. */
  chart: (patientId: string) => ['chart', patientId] as const,
  /** B2 — işlem geçmişi: prototipin `patient.islemler` alanını besler. */
  procedures: (patientId: string) => ['procedures', patientId] as const,
  /** B2 — HSYS katalogu; arama metni ve grup süzgeci anahtarın parçasıdır. */
  procedureCodes: (query: string, group: string | null) =>
    ['procedure-codes', query, group] as const,
  icd10Codes: (query: string) => ['icd10-codes', query] as const,
  mbysEntries: ['mbys-entries'] as const,
  /** B3 — tedavi planları: Planlar sayfasının tek sayfalık listesi (KPI sayaçları dâhil). */
  plans: ['plans'] as const,
  /** B3 — tek plan (kalemleriyle); plan kimliğiyle okunur. */
  plan: (planId: string) => ['plan', planId] as const,
  /**
   * B3 — hasta kartındaki plan: hastanın iptal edilmemiş EN YENİ planı. Anahtar `patient`
   * önekinin ALTINDA değildir; olsaydı B1'in hasta olayı planı da geçersizlerdi.
   */
  patientPlan: (patientId: string) => ['patient-plan', patientId] as const,
  /** B3 — fiyat listesi: "Plana işlem ekle" modalinin kaynağı; gün içinde değişmez. */
  priceList: ['price-list'] as const,
  /**
   * Sürüm 2 — not şablonları (`GET /note-templates?kind=`); tür anahtarın sonuna eklenir. Ayarlar ve
   * seçici aynı önekle tazelenir (`note_template.updated`).
   */
  noteTemplates: ['note-templates'] as const,
  /** Ayarlar › Fiyat listesi tablosu; `['price-list']` önekiyle birlikte tazelenir. */
  priceListAdmin: ['price-list', 'admin'] as const,
  /** Bir kodun fiyat geçmişi penceresi. */
  priceHistory: (code: string) => ['price-list', 'history', code] as const,
  /** Teklif önizlemesi (`GET /plans/:id/quote`): taksit sayısı anahtarın parçasıdır. */
  planQuote: (planId: string, installments: number, addendum: number | null = null) =>
    ['plan-quote', planId, installments, addendum] as const,
  /** Plan başlığının hekim seçicisi (`GET /plans/dentists`). */
  planDentists: ['plan-dentists'] as const,
  /** B4 — takvim: aralık anahtarın parçasıdır, imleç kayınca ayrı sorgu açılır. */
  appointments: (from: string, to: string) => ['appointments', from, to] as const,
  /** Aralıktan bağımsız referans anlık görüntüsü: `D.ODALAR`/`D.HEKIMLER` bunu okur. */
  appointmentRefs: ['appointment-refs'] as const,
  waitlist: ['waitlist'] as const,
  bookingRequests: ['booking-requests'] as const,
  lobbyBoard: ['lobby-board'] as const,
  /**
   * B5 — hareket listesi SUNUCUDA süzülür ve sayfalanır (`useInfiniteQuery`, hasta listesiyle
   * aynı kalıp: data/patients.ts). Arama metni ve durum süzgeci anahtarın parçasıdır; olmasaydı
   * ekran süzgeci değiştirdiğinde önceki süzgecin sonucunu gösterirdi.
   *
   * Eskiden anahtar parametresizdi ve süzgeçler İSTEMCİDE, yalnızca inen 100 satır üzerinde
   * çalışıyordu: arama kutusu eski bir tahsilatı bulamıyor, kullanıcı da "yok" sanıyordu.
   */
  payments: (q: string, status: string | null) => ['payments', q, status] as const,
  /** Süzgeçten bağımsız ÖN EK: tazeleme (invalidate) bütün süzgeç çeşitlerini kapsar. */
  paymentsAll: ['payments'] as const,
  /** KPI şeridi: toplamları sunucu hesaplar, ekran satırlardan toplamaz (spec §9.1). */
  paymentSummary: ['payment-summary'] as const,
  debtors: ['debtors'] as const,
  /** Ufuk anahtarın parçasıdır: 30 günlük KPI ile takvim sekmesi ayrı sorgulardır. */
  installments: (horizonDays: number) => ['installments', horizonDays] as const,
  /**
   * B5 — hasta finans kartı. Anahtar `patient` ön ekinin ALTINDA DEĞİLDİR (H37): olsaydı
   * B1'in her hasta olayı bu A sınıfı okumayı da yeniden tetiklerdi ve her tetikleme
   * denetime bir satır yazardı (auditRead, spec §4.4). `patientPlan` ile aynı gerekçe.
   */
  patientFinance: (patientId: string) => ['patient-finance', patientId] as const,
  /**
   * Hasta kartının hesap hareketleri (yaşam döngüsü §4.11.12): sayfa anahtarın parçasıdır. Finans
   * kartı gibi `patient` ön ekinin ALTINDA DEĞİLDİR (H37): her açılış denetime bir satır yazar
   * (`payments.patient_statement.read`), hasta olayı bu okumayı yeniden tetiklemesin. Para olayı
   * ön eki toptan tazeler (`MONEY_QUERY_KEYS`).
   */
  patientStatement: (patientId: string, page: number) =>
    ['patient-statement', patientId, page] as const,
  /**
   * Makbuz / ekstre gönderim penceresinin gönderilebilirlik okuması (PDF yeniden tasarımı C31).
   * Pencere her açılışta TEK kez okur (önbellek tutulmaz: adres ve arşiv durumu değişmiş olabilir).
   */
  documentSendOptions: (patientId: string, kind: string) =>
    ['document-send-options', patientId, kind] as const,
  /**
   * Hastanın planları (tahsilat penceresinin "Plan" seçicisi). `plans` ön ekinin altındadır:
   * plan yazması (kabul, iptal, tamamlama) seçiciyi de tazeler.
   */
  patientPlanList: (patientId: string) => ['plans', 'patient', patientId] as const,
  /** Tek tahsilat: hesap hareketi menüsünden açılan iade / iptal pencereleri sürümü buradan okur. */
  payment: (paymentId: string) => ['payment', paymentId] as const,
  /** B6 — Bugün: gösterge paneli tek istekte döner, `staleTime`/SSE ile tazelenir. */
  dashboard: ['dashboard'] as const,
  /** B6 — Gelen kutusu ve zil: liste ve okunmamış sayısı aynı yanıtta gelir. */
  notifications: ['notifications'] as const,
  /** B6 — bildirim tercihleri paneli (shell-v4.jsx sağ panel). */
  notificationRules: ['notification-rules'] as const,
  // D1 lab
  /** D1 — laboratuvar panosu: tek sorgu (sayfalar sorgu içinde birleşir), hasta başına bölünmez. */
  labJobs: ['lab-jobs'] as const,
  /** D1 — iş emri formunun etkin laboratuvar listesi. */
  labs: ['labs'] as const,
  /** D1 — Ayarlar → Laboratuvarlar: pasifler dâhil. `['labs']` öneki ikisini birden tazeler. */
  labsAll: ['labs', 'all'] as const,
  // ── C4 reçete ──────────────────────────────────────────────────────────────────────────────
  /** C4 — reçete şablonları (rx_templates): klinik verisi değil, gün içinde değişmez. */
  rxTemplates: ['rx-templates'] as const,
  /**
   * C4 — hasta kartındaki reçete geçmişi. Olayın `resourceId`'si HASTA kimliğidir
   * (prescriptions/service.ts `publish`), yani canlı olay tam bu anahtarı tazeler. `patient`
   * önekinin altında DEĞİLDİR: hasta olayı reçete geçmişini (denetime düşen okuma) yeniden çekmez.
   */
  prescriptions: (patientId: string) => ['prescriptions', patientId] as const,
  /** C4 — tek reçete (iptal penceresi `version`'ı buradan okur). */
  prescription: (id: string) => ['prescription', id] as const,
  /** C4 — Entegrasyonlar sayfası: altı tür, tek istek. */
  integrations: ['integrations'] as const,
  /**
   * WhatsApp hazır — Entegrasyonlar → WhatsApp → Şablonlar (Meta durumu). `integrations`
   * ön ekinin ALTINDA DEĞİLDİR: sağlayıcı listesi her tazelendiğinde Meta durumları da yeniden
   * inmesin; ilişki `events.ts`'te açıkça kurulur.
   */
  whatsappTemplates: ['whatsapp-templates'] as const,
  /**
   * WhatsApp birlikte kullanım — Entegrasyonlar → WhatsApp durum kartı (`GET …/onboarding`).
   * `integrations` ön ekinin altında DEĞİLDİR (şablonlarla aynı gerekçe); `integration.updated`
   * olayı onu da açıkça tazeler (events.ts): geçmiş/rehber webhook'ları ve hesap olayları bu olayı
   * yayar, kart sayfa açıkken ilerler.
   */
  whatsappOnboarding: ['whatsapp-onboarding'] as const,
  // ── C1 e-fatura ────────────────────────────────────────────────────────────────────────────
  /** C1 — e-belge listesi (süzgeç istemcide; sayfa sunucudan birleşik gelir). */
  invoices: ['invoices'] as const,
  /** C1 — dört KPI ve KDV oranı: sunucu hesaplar. */
  invoiceSummary: ['invoice-summary'] as const,
  /** C1 — deneme şeridinin kaynağı (banko `integrations:read` taşımaz). */
  invoiceProvider: ['invoice-provider'] as const,
  /** C1 — önizleme kartı: kalemler, düzenleyen VKN'si, maskeli T.C. */
  invoice: (id: string) => ['invoice', id] as const,
  // ── C2 raporlar ────────────────────────────────────────────────────────────────────────────
  /** Ay sayısı anahtarın parçasıdır: 3/6/12 aralığı ayrı sorgudur (sunucu ekseni o uzunlukta kurar). */
  reportFinance: (months: number) => ['reports', 'finance', months] as const,
  reportClinical: (months: number) => ['reports', 'clinical', months] as const,
  reportPatients: (months: number) => ['reports', 'patients', months] as const,
  /** Canlı KPI: 3,2 saniyede bir tazelenir, aralıktan bağımsızdır. */
  reportLive: ['reports', 'live'] as const,
  reportQuotes: (days: number) => ['reports', 'quotes', days] as const,
  /**
   * WhatsApp hazır — Raporlar → Memnuniyet. `reports` ÖN EKİNİN ALTINDADIR: "Tekrar dene" ve
   * rapor yenilemesi (`reportsAll`) bu sorguyu da tazeler.
   */
  reportSurveys: (from: string, to: string) => ['reports', 'surveys', from, to] as const,
  /** Hasta kartındaki "Memnuniyet" özeti. */
  patientSurveys: (patientId: string) => ['patient-surveys', patientId] as const,
  /** Yenileme ve para olayları BÜTÜN rapor sorgularını bayatlatır. */
  reportsAll: ['reports'] as const,
  /** C2 — gider listesi; ay penceresi anahtarın parçasıdır. */
  expenses: (months: number) => ['expenses', months] as const,
  expensesAll: ['expenses'] as const,
  // ── D2 stok & ekipman ──────────────────────────────────────────────────────────────────────
  /** D2 — stok listesi: tek sorgu, KPI'lar ve kategori çipleri aynı yanıtta gelir. */
  stockItems: ['stock-items'] as const,
  /**
   * D2 — kalem başına hareket geçmişi. `stock-items` ÖN EKİNİN ALTINDA DEĞİLDİR: olsaydı her
   * hareket yazmasında kapalı çekmecelerin geçmişi de yeniden inerdi.
   */
  stockMovements: (itemId: string) => ['stock-movements', itemId] as const,
  /** Süzgeçten bağımsız ön ek: tek hareket bütün açık geçmişleri bayatlatır. */
  stockMovementsAll: ['stock-movements'] as const,
  /** D2 — ekipman listesi: kartlar, dört KPI ve son validasyon aynı yanıtta. */
  equipment: ['equipment'] as const,
  equipmentServiceLog: (equipmentId: string) => ['equipment-service-log', equipmentId] as const,
  equipmentServiceLogAll: ['equipment-service-log'] as const,
  // ── D3 mesajlaşma & otomasyon ────────────────────────────────────────────────────────────────
  /** Sohbet listesi; akış AYRI anahtardadır (liste tazelenince açık akış yeniden çekilmesin). */
  conversations: ['conversations'] as const,
  thread: (conversationId: string) => ['thread', conversationId] as const,
  /** Gelen mesaj olayı hangi sohbete düştüğünü söylese de açık akış kimliğiyle eşlenmeli. */
  threadAll: ['thread'] as const,
  /**
   * WhatsApp tanınmayan numaralar (spec 2026-10-02 §4.11): hastayla eşleşmeyen numaraların
   * yazışma listesi ve akışı. Hasta sohbetinin anahtarlarından AYRIDIR (`['thread']` öneki
   * bunları kapsamaz); `conversations` olayı ikisini de açıkça bayatlatır (data/events.ts).
   */
  unknownChats: ['unknown-chats'] as const,
  unknownThread: (contactId: string) => ['unknown-thread', contactId] as const,
  /** Açık tanınmayan akışların ön eki: tanınmayan olayı kişiyi söylemez (`resourceId: null`). */
  unknownThreadAll: ['unknown-thread'] as const,
  /** Sağlayıcı durumu: yazma alanını ve "Akıllı yanıt" düğmesini kapatan tek kaynak. */
  messagingStatus: ['messaging-status'] as const,
  messageTemplates: ['message-templates'] as const,
  automationRules: ['automation-rules'] as const,
  automationRuns: (ruleId: string) => ['automation-runs', ruleId] as const,
  automationRunsAll: ['automation-runs'] as const,
  /** Recall kuyruğu süzgeci anahtarın parçasıdır (prototipin `seg` düğmeleri). */
  recallQueue: (status: string) => ['recall', status] as const,
  recallAll: ['recall'] as const,
  noshowRisk: ['noshow-risk'] as const,
  /** v1 — yedek koşularının salt okunur özeti (`GET /backup/status`). */
  backupStatus: ['backup-status'] as const,
};

/**
 * Para değişince tazelenecek anahtarların TEK listesi. İki yerden okunur: yazma sonrası
 * (`data/payments.ts` → `invalidateMoney`) ve canlı olayda (`data/events.ts` → `payments`).
 * Ayrı ayrı sayılsalardı biri değişip diğeri kalır ve fark SESSİZ olurdu: ekranın bir yarısı
 * tazelenir, öbür yarısı bayat kalırdı.
 *
 * `patients` da listede: hasta listesi `balanceKurus` taşır (contracts/patients.ts:137) ve
 * para girince o sütun anında bayatlar.
 */
export const MONEY_QUERY_KEYS: readonly (readonly unknown[])[] = [
  queryKeys.paymentsAll,
  queryKeys.paymentSummary,
  queryKeys.debtors,
  ['installments'],
  ['patient-finance'],
  ['patient-statement'],
  ['payment'],
  ['patients'],
];

export function DentQueryProvider({ children }: { children: ReactNode }) {
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
